// src/Components/DocumentUploadModal.js
import React, { useState, useRef, useEffect } from "react";
import styles from "../Styles/DocumentsUploadModal.module.css";

/**
 * props:
 *  - isOpen: bool
 *  - onClose: () => void
 *  - onUpload: (file, { category, visibility }) => Promise<void>
 *  - categories: string[]
 */
export default function DocumentUploadModal({
  isOpen,
  onClose,
  onUpload,
  categories = [],
}) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(categories[0] || "");
  const [visibility, setVisibility] = useState("hallitus"); // 👈 oletus
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  /* Reset state kun modaali suljetaan */
  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setCategory(categories[0] || "");
      setVisibility("hallitus");
      setSubmitting(false);
    }
  }, [isOpen, categories]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
  };

  const handleSubmit = async () => {
    if (!file || !category) return;

    setSubmitting(true);
    try {
      await onUpload(file, {
        category,
        visibility,
      });
      onClose();
    } catch (e) {
      console.error("Upload failed:", e);
      alert("Dokumentin tallennus epäonnistui.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = () => {
    if (!submitting) onClose();
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <>
      <div className={styles.modalBackdrop} onClick={handleBackdropClick} />
      <div
        className={styles.modalRoot}
        role="dialog"
        aria-modal="true"
        onClick={handleBackdropClick}
      >
        <div className={styles.modalCard} onClick={stopPropagation}>
          <button
            className={styles.modalClose}
            onClick={onClose}
            disabled={submitting}
          >
            ×
          </button>

          <h3 className={styles.modalTitleLarge}>Lisää dokumentti</h3>

          {/* TIEDOSTO */}
          <div className={styles.formRow}>
            <label className={styles.label}>Tiedosto</label>
            <input
              ref={fileInputRef}
              type="file"
              className={styles.input}
              onChange={handleFileChange}
            />
            {file && (
              <div className={styles.mutedSmall}>
                Valittu: <strong>{file.name}</strong>
              </div>
            )}
          </div>

          {/* KATEGORIA */}
          <div className={styles.formRow}>
            <label className={styles.label}>Kategoria</label>
            <select
              className={styles.input}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* NÄKYVYYS */}
          <div className={styles.formRow}>
            <label className={styles.label}>Dokumentin näkyvyys</label>
            <select
              className={styles.input}
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
            >
              <option value="hallitus">Vain hallitus</option>
              <option value="osakas">Osakkaat</option>
              <option value="asukas_osakas">Asukkaat ja osakkaat</option>
            </select>
          </div>

          {/* TOIMINNOT */}
          <div className={styles.formRow}>
            <button
              className={styles.primaryBtn}
              onClick={handleSubmit}
              disabled={!file || !category || submitting}
            >
              {submitting ? "Tallennetaan…" : "Tallenna"}
            </button>
            <button
              className={styles.cancelBtnTight}
              onClick={onClose}
              disabled={submitting}
            >
              Peruuta
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
